import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";

function formatApiError(detail) {
    if (detail == null) return "Sign in failed";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function AdminLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email.trim(), password);
            toast.success(`Signed in as ${user.name || user.email}`);
            if (user.role === "district_admin" && user.year_id) {
                navigate(`/admin/year/${user.year_id}`);
            } else if (user.role === "super_admin") {
                navigate("/admin/platform");
            } else {
                navigate("/admin");
            }
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Sign in failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="admin-login-page">
            {/* Left visual panel */}
            <div className="relative hidden lg:block">
                <img
                    src="https://images.unsplash.com/photo-1740750958991-19f18db5a676?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBkaXZlcnNlJTIwcGVvcGxlJTIwZ2F0aGVyaW5nJTIwYWJzdHJhY3QlMjBibHVyfGVufDB8fHx8MTc4MzcwNjYwMnww&ixlib=rb-4.1.0&q=85"
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-rotary-blue/70" />
                <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/10 ring-1 ring-white/30 backdrop-blur">
                            <ShieldCheck className="h-5 w-5 text-rotary-gold" />
                        </div>
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.25em] text-white/75 leading-none">Admin</p>
                            <p className="rotary-heading text-xl leading-tight">Member Directory</p>
                        </div>
                    </div>
                    <div className="max-w-md space-y-4">
                        <p className="rotary-heading text-4xl leading-[1.05]">
                            Keep your directory <span className="text-rotary-gold">current.</span>
                        </p>
                        <p className="text-white/85">
                            Manage members, chase submissions, and generate a directory PDF — all from one place.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right form panel */}
            <div className="flex items-center justify-center px-6 py-16 noise-bg">
                <form
                    onSubmit={submit}
                    className="w-full max-w-md rounded-2xl border bg-white p-8 shadow-sm animate-fade-up"
                >
                    <div className="mb-8">
                        <span className="label-eyebrow">Restricted area</span>
                        <h1 className="rotary-heading mt-1 text-3xl text-rotary-ink">Admin Sign in</h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Enter your admin credentials to access the directory.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="label-eyebrow">Email</Label>
                            <div className="relative mt-2">
                                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@yourdomain.com"
                                    className="pl-9 h-11"
                                    data-testid="admin-email-input"
                                    autoComplete="email"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="password" className="label-eyebrow">Password</Label>
                            <div className="relative mt-2">
                                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pl-9 h-11"
                                    data-testid="admin-password-input"
                                    autoComplete="current-password"
                                    required
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-rotary-blue text-white hover:bg-rotary-sky rounded-full"
                            data-testid="admin-login-submit-btn"
                        >
                            {loading ? "Signing in…" : (<>Sign in <ArrowRight className="ml-1.5 h-4 w-4" /></>)}
                        </Button>

                        <div className="flex justify-end">
                            <Link
                                to="/admin/forgot-password"
                                className="text-xs text-rotary-blue hover:underline"
                                data-testid="admin-forgot-password-link"
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </div>

                    <p className="mt-6 text-xs text-muted-foreground">
                        Not an admin? Ask your directory contact for a personal profile link.
                    </p>
                </form>
            </div>
        </div>
    );
}
